import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import AppContext from "../../../../context/AppContext";
import useTspPermission from "../../hooks/useTspPermission";
import { getGroupedModules } from "../../navigation/tspMenu";
import TspNav from "./TspNav";

// TSP's own header — replaces the shared ICC Header on /tsp/* routes (per the
// approved Phase A plan) and matches the approved header design: brand, search,
// profile, then the primary nav. Reuses AppContext's real `user` (shared infra)
// instead of inventing new state. There is no notification bell in the header
// (removed on request); Notifications stays reachable from the profile menu.
//
// The primary tabs are never permission-filtered (see PRIMARY_NAV_ITEMS in
// navigation/tspMenu.js). Modules that are not one of them live in the profile
// menu ("TSP modules", which IS permission-filtered), so they stay reachable
// without adding anything to the design's nav row.
//
// Phones (<= 767px, styles only): the search box and the tab row fold into a
// dropdown opened by the menu button, so the sticky header stays one compact
// row. The tabs and the search form are the same elements at every width; the
// button only toggles a class, and it is hidden by CSS on wider screens.

// "Prithvi Sharma" -> "PS" (the design shows two-letter initials).
function initialsOf(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

export default function TspHeader() {
  const { user } = useContext(AppContext);
  const { hasPermission } = useTspPermission();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const profileRef = useRef(null);
  const headerRef = useRef(null);
  const { pathname } = useLocation();
  const modules = getGroupedModules(hasPermission);

  // Close the profile menu on outside click or Escape.
  useEffect(() => {
    if (!profileOpen) return undefined;
    const onPointerDown = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) setProfileOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setProfileOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [profileOpen]);

  // The phone menu closes on navigation, outside click or Escape.
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) setMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const userLogOut = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    setMenuOpen(false);
    navigate(trimmed ? `/tsp/search?q=${encodeURIComponent(trimmed)}` : "/tsp/search");
  };

  return (
    <header ref={headerRef} className={`tsp-header${menuOpen ? " is-menu-open" : ""}`}>
      <div className="tsp-header-top">
        <Link to="/tsp" className="tsp-brand">
          <img src="/images/common/logo.png" alt="ISKCON" className="tsp-brand-logo" />
          <span className="tsp-brand-text">
            <span className="tsp-brand-title">Travelling Sankirtan Party</span>
            <span className="tsp-brand-subtitle">MANAGEMENT APPLICATION</span>
          </span>
        </Link>

        <div className="tsp-header-actions">
          <form className="tsp-search" role="search" onSubmit={handleSearch}>
            <Search size={15} aria-hidden="true" />
            <input type="search" placeholder="Search..." aria-label="Search TSP" value={query} onChange={(event) => setQuery(event.target.value)} />
          </form>

          <div className="tsp-profile" ref={profileRef}>
            <button type="button" className="tsp-profile-trigger" aria-haspopup="menu" aria-expanded={profileOpen} onClick={() => setProfileOpen((prev) => !prev)}>
              <span className="tsp-profile-avatar" aria-hidden="true">
                {initialsOf(user?.name)}
              </span>
              <span className="tsp-profile-text">
                <span className="tsp-profile-hi" title={user?.name}>
                  Hi {user?.name}
                </span>
                <span className="tsp-profile-role" title={user?.role}>
                  {user?.role}
                </span>
              </span>
              <span className="tsp-caret" aria-hidden="true" />
            </button>

            {profileOpen && (
              <div className="tsp-profile-panel" role="menu">
                {modules.length > 0 && (
                  <div className="tsp-modules-menu">
                    <span className="tsp-modules-title">TSP modules</span>
                    {modules.map((group) => (
                      <div key={group.name} className="tsp-modules-group">
                        <span className="tsp-modules-group-name">{group.name}</span>
                        <ul>
                          {group.items.map((item) => (
                            <li key={item.link}>
                              <Link to={item.link} role="menuitem" className="tsp-profile-item" onClick={() => setProfileOpen(false)}>
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
                <div className="tsp-profile-account">
                  <Link to="/my-profile" role="menuitem" className="tsp-profile-item" onClick={() => setProfileOpen(false)}>
                    My Profile
                  </Link>
                  <button type="button" role="menuitem" className="tsp-profile-item" onClick={userLogOut}>
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            className="tsp-menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="tsp-header-menu"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            {menuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>
        </div>
      </div>

      <div className="tsp-nav-row" id="tsp-header-menu">
        <TspNav />
      </div>
    </header>
  );
}
